import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from "../components/ui/table";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return ymd(d); };

export default function Reports() {
  const { t, lang } = useI18n();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(ymd(new Date()));
  const [rows, setRows] = useState([]);

  const range = () => ({
    start: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    end: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  });

  const exportExcel = async () => {
    try {
      const { data } = await api.get("/reports/suppliers/export", { params: { ...range(), lang, from_label: from, to_label: to }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `supplier_report_${from || "all"}_${to || "all"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(() => {
    const params = range();
    api.get("/reports/suppliers", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [from, to]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sum = (k) => rows.reduce((a, r) => a + r[k], 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl sm:text-5xl font-extrabold">{t("supplierReport")}</h1>
        <p className="text-slate-500 mt-2">{t("reportHint")}</p>
      </div>
      <div className="flex flex-wrap items-end gap-3 bg-white border rounded-lg p-4 w-fit">
        <div className="space-y-1.5"><Label>{t("fromDate")}</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from-input" /></div>
        <div className="space-y-1.5"><Label>{t("toDate")}</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to-input" /></div>
        <Button onClick={load} data-testid="report-apply-btn">{t("apply")}</Button>
        <Button onClick={exportExcel} data-testid="report-export-btn" className="bg-[#F5BE25] hover:bg-[#E5AC12] text-[#3E3E40]"><Download className="w-4 h-4" />{t("exportExcel")}</Button>
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto max-w-4xl">
        <Table>
          <TableHeader><TableRow>
            {["supplier", "receivedCount", "dispatchedCount", "inStock"].map((h) => <TableHead key={h} className="text-start">{t(h)}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-10">{t("noData")}</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.supplier_name} data-testid={`report-row-${r.supplier_name}`}>
                <TableCell className="font-bold">{r.supplier_name}</TableCell>
                <TableCell><span className="inline-block min-w-8 text-center rounded-full bg-sky-100 text-sky-900 font-bold px-2" data-testid={`report-received-${r.supplier_name}`}>{r.received}</span></TableCell>
                <TableCell><span className="inline-block min-w-8 text-center rounded-full bg-emerald-100 text-emerald-900 font-bold px-2" data-testid={`report-dispatched-${r.supplier_name}`}>{r.dispatched}</span></TableCell>
                <TableCell><span className="inline-block min-w-8 text-center rounded-full bg-rose-100 text-rose-900 font-bold px-2" data-testid={`report-instock-${r.supplier_name}`}>{r.in_stock}</span></TableCell>
              </TableRow>
            ))}
          </TableBody>
          {rows.length > 0 && (
            <TableFooter><TableRow>
              <TableCell className="font-extrabold">{t("totalRow")}</TableCell>
              <TableCell className="font-extrabold" data-testid="report-total-received">{sum("received")}</TableCell>
              <TableCell className="font-extrabold" data-testid="report-total-dispatched">{sum("dispatched")}</TableCell>
              <TableCell className="font-extrabold" data-testid="report-total-instock">{sum("in_stock")}</TableCell>
            </TableRow></TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
}
